import { createTheme } from "@mui/material/styles";
import { DESIGN_TOKENS, getDesignColors } from "./designTokens";

const { shape, size, motion, typography: fonts } = DESIGN_TOKENS;

const typography = {
  fontFamily: fonts.fontFamily,
  fontSize: 14,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 600,
  h1: {
    fontFamily: fonts.displayFontFamily,
    fontSize: "clamp(2.25rem, 1.2rem + 3.6vw, 4.25rem)",
    fontWeight: 600,
    lineHeight: 1.05,
    letterSpacing: "-0.012em",
  },
  h2: {
    fontFamily: fonts.displayFontFamily,
    fontSize: "clamp(1.85rem, 1.05rem + 2.6vw, 3.25rem)",
    fontWeight: 600,
    lineHeight: 1.08,
    letterSpacing: "-0.008em",
  },
  h3: {
    fontFamily: fonts.displayFontFamily,
    fontSize: "clamp(1.55rem, 1rem + 1.7vw, 2.35rem)",
    fontWeight: 600,
    lineHeight: 1.12,
    letterSpacing: "-0.006em",
  },
  h4: {
    fontFamily: fonts.displayFontFamily,
    fontSize: "clamp(1.3rem, 1rem + 0.9vw, 1.75rem)",
    fontWeight: 600,
    lineHeight: 1.18,
    letterSpacing: "-0.004em",
  },
  h5: {
    fontFamily: fonts.displayFontFamily,
    fontSize: "1.3125rem",
    fontWeight: 600,
    lineHeight: 1.25,
    letterSpacing: 0,
  },
  h6: {
    fontFamily: fonts.displayFontFamily,
    fontSize: "1.125rem",
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: 0,
  },
  subtitle1: {
    fontSize: "1rem",
    fontWeight: 500,
    lineHeight: 1.45,
    letterSpacing: 0,
  },
  subtitle2: {
    fontSize: "0.9375rem",
    fontWeight: 600,
    lineHeight: 1.45,
    letterSpacing: 0,
  },
  body1: {
    fontSize: "0.9375rem",
    lineHeight: 1.55,
    letterSpacing: 0,
  },
  body2: {
    fontSize: "0.875rem",
    lineHeight: 1.5,
    letterSpacing: 0,
  },
  button: {
    fontWeight: 500,
    letterSpacing: 0,
    textTransform: "none",
  },
  caption: {
    fontSize: "0.75rem",
    lineHeight: 1.45,
    letterSpacing: 0,
  },
  overline: {
    fontSize: "0.75rem",
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
};

export default function createTableTheme(mode = "light") {
  const isDark = mode === "dark";
  const colors = getDesignColors(mode);
  const mobileQuery = "@media (max-width: 640px)";
  const formControlHeight = 44;
  const formControlHeightSmall = formControlHeight;
  const {
    buttonHeight,
    buttonHeightSmall,
    controlHeight,
    iconButton,
    iconButtonSmall,
  } = size;

  return createTheme({
    typography,
    // Base unit for sx `borderRadius: n` multipliers; components set explicit radii below.
    shape: { borderRadius: shape.controlRadius },
    designTokens: DESIGN_TOKENS,
    appColors: colors,
    palette: {
      mode,
      primary: {
        main: colors.primary,
        light: isDark ? "#64aaff" : "#3d94ea",
        dark: isDark ? "#0060df" : "#0056b3",
        contrastText: colors.primaryContrast,
      },
      secondary: {
        main: colors.accent,
        contrastText: colors.primaryContrast,
      },
      success: { main: isDark ? "#30d158" : "#1d8a4e" },
      warning: { main: isDark ? "#ffd60a" : "#b25000" },
      error: { main: colors.error },
      info: { main: colors.link },
      background: {
        default: colors.background,
        paper: colors.paper,
      },
      text: {
        primary: colors.text,
        secondary: colors.textSecondary,
        disabled: colors.textTertiary,
      },
      divider: colors.divider,
      action: {
        hover: colors.primarySoft,
        selected: colors.primarySoft,
        disabledBackground: isDark
          ? "rgba(255, 255, 255, 0.08)"
          : "rgba(0, 0, 0, 0.06)",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            backgroundColor: colors.background,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            scrollBehavior: "smooth",
          },
          body: {
            backgroundColor: colors.background,
            color: colors.text,
            fontFeatureSettings: '"kern" 1',
            fontVariantNumeric: "lining-nums tabular-nums",
          },
          "::selection": {
            color: colors.primaryContrast,
            backgroundColor: colors.primary,
          },
          "*:focus-visible": {
            outline: `2px solid ${colors.primary}`,
            outlineOffset: 2,
          },
          "@media (prefers-reduced-motion: reduce)": {
            "*": {
              animationDuration: "0.01ms !important",
              animationIterationCount: "1 !important",
              transitionDuration: "0.01ms !important",
              scrollBehavior: "auto !important",
            },
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingInline: 22,
            [mobileQuery]: { paddingInline: 18 },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true, disableRipple: false },
        styleOverrides: {
          root: {
            minHeight: buttonHeight,
            borderRadius: shape.pillRadius,
            paddingInline: 18,
            fontWeight: 500,
            fontSize: "0.9375rem",
            textTransform: "none",
            letterSpacing: 0,
            transition: `background-color ${motion.fast}, border-color ${motion.fast}, color ${motion.fast}, transform ${motion.fast}, box-shadow ${motion.fast}`,
            "&:active": { transform: "scale(0.975)" },
            "&.Mui-disabled": { opacity: 0.45 },
          },
          sizeSmall: {
            minHeight: buttonHeightSmall,
            paddingInline: 14,
            fontSize: "0.8438rem",
          },
          sizeLarge: {
            minHeight: 48,
            paddingInline: 26,
            fontSize: "1.0625rem",
          },
          contained: {
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          },
          containedPrimary: {
            color: colors.primaryContrast,
            backgroundColor: colors.primary,
            "&:hover": { backgroundColor: colors.primaryHover },
          },
          containedSecondary: {
            color: colors.primaryContrast,
            backgroundColor: colors.accent,
          },
          outlined: {
            borderColor: colors.divider,
            color: colors.text,
            "&:hover": {
              borderColor: colors.primary,
              color: colors.primary,
              backgroundColor: colors.primarySoft,
            },
          },
          outlinedPrimary: {
            borderColor: colors.primary,
            color: colors.primary,
            "&:hover": {
              borderColor: colors.primary,
              backgroundColor: colors.primarySoft,
            },
          },
          text: {
            paddingInline: 12,
            color: colors.link,
            "&:hover": { backgroundColor: colors.primarySoft },
          },
        },
        variants: [
          {
            props: { variant: "glass" },
            style: {
              color: colors.text,
              backgroundColor: colors.glass,
              border: `1px solid ${colors.glassBorder}`,
              backdropFilter: "saturate(180%) blur(20px)",
              WebkitBackdropFilter: "saturate(180%) blur(20px)",
              "&:hover": {
                backgroundColor: isDark
                  ? "rgba(44, 44, 46, 0.86)"
                  : "rgba(255, 255, 255, 0.92)",
              },
            },
          },
        ],
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            width: iconButton,
            height: iconButton,
            padding: 6,
            borderRadius: shape.controlRadius,
            color: colors.textSecondary,
            transition: `background-color ${motion.fast}, color ${motion.fast}, transform ${motion.fast}`,
            "&:hover": {
              color: colors.primary,
              backgroundColor: colors.primarySoft,
            },
            "&:active": { transform: "scale(0.94)" },
            "&.Mui-disabled": {
              color: colors.textTertiary,
              opacity: 0.45,
            },
          },
          sizeSmall: {
            width: iconButtonSmall,
            height: iconButtonSmall,
            padding: 5,
          },
          colorInherit: {
            color: "inherit",
            "&:hover": { color: "inherit" },
          },
          colorError: {
            color: colors.error,
            "&:hover": {
              color: colors.error,
              backgroundColor: isDark
                ? "rgba(255, 69, 58, 0.16)"
                : "rgba(208, 52, 44, 0.10)",
            },
            "&.Mui-disabled": { color: colors.textTertiary },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: shape.pillRadius,
            borderColor: colors.divider,
            textTransform: "none",
            fontWeight: 500,
            "&.Mui-selected": {
              color: colors.primaryContrast,
              backgroundColor: colors.primary,
              "&:hover": { backgroundColor: colors.primaryHover },
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: "outlined",
          size: "medium",
          slotProps: { inputLabel: { shrink: true } },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: colors.textSecondary,
            fontSize: "0.875rem",
            letterSpacing: 0,
            "&.Mui-focused": { color: colors.primary },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          shrink: { fontWeight: 500 },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: { marginInline: 4, letterSpacing: 0 },
        },
      },
      MuiFormControl: {
        defaultProps: { size: "medium" },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginLeft: 0,
            marginRight: 16,
            gap: 4,
            "&:last-of-type": { marginRight: 0 },
          },
        },
      },
      MuiRadioGroup: {
        styleOverrides: {
          root: { rowGap: 8, columnGap: 16 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            minHeight: formControlHeight,
            borderRadius: shape.controlRadius,
            backgroundColor: colors.input,
            transition: `border-color ${motion.fast}, box-shadow ${motion.fast}, background-color ${motion.fast}`,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.divider,
              transition: `border-color ${motion.fast}`,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? "#48484a" : "#b0b0b8",
            },
            "&.Mui-focused": {
              boxShadow: `0 0 0 4px ${colors.primarySoft}`,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.primary,
              borderWidth: 1.5,
            },
            "&.Mui-error.Mui-focused": {
              boxShadow: `0 0 0 4px ${isDark ? "rgba(255,69,58,0.18)" : "rgba(208,52,44,0.12)"}`,
            },
          },
          sizeSmall: {
            minHeight: formControlHeightSmall,
            borderRadius: shape.controlRadius,
            "& .MuiOutlinedInput-input": { paddingBlock: 6 },
          },
          input: {
            boxSizing: "border-box",
            paddingBlock: 10,
            fontVariantNumeric: "lining-nums tabular-nums",
            "&::placeholder": {
              color: colors.textTertiary,
              opacity: 1,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            minHeight: formControlHeight,
            color: colors.text,
            "&.MuiInputBase-sizeSmall": { minHeight: formControlHeightSmall },
            "&.MuiInputBase-multiline": { minHeight: 0 },
          },
        },
      },
      MuiAutocomplete: {
        defaultProps: { size: "medium" },
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              paddingBlock: 0,
              minHeight: formControlHeight,
              "&.MuiInputBase-sizeSmall": {
                minHeight: formControlHeightSmall,
              },
            },
            "& .MuiAutocomplete-input": {
              paddingBlock: "10px !important",
            },
            "& .MuiInputBase-sizeSmall .MuiAutocomplete-input": {
              paddingBlock: "6px !important",
            },
          },
          inputRoot: { paddingBlock: "0 !important" },
          tag: { margin: 3, maxHeight: 28 },
          paper: {
            borderRadius: 14,
            border: `1px solid ${colors.divider}`,
            boxShadow: colors.shadowMd,
            backgroundImage: "none",
          },
          listbox: { padding: 6 },
          option: {
            minHeight: formControlHeightSmall,
            paddingBlock: 10,
            borderRadius: 8,
          },
        },
      },
      // x-date-pickers v8 renders its own PickersTextField, which does not pick
      // up MuiOutlinedInput styles — mirror them so dates match every other field.
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            minHeight: formControlHeight,
            borderRadius: shape.controlRadius,
            backgroundColor: colors.input,
            color: colors.text,
            transition: `border-color ${motion.fast}, box-shadow ${motion.fast}, background-color ${motion.fast}`,
            "& .MuiPickersOutlinedInput-notchedOutline": {
              borderColor: colors.divider,
              transition: `border-color ${motion.fast}`,
            },
            "&:hover .MuiPickersOutlinedInput-notchedOutline": {
              borderColor: isDark ? "#48484a" : "#b0b0b8",
            },
            "&.Mui-focused": {
              boxShadow: `0 0 0 4px ${colors.primarySoft}`,
            },
            "&.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
              borderColor: colors.primary,
              borderWidth: 1.5,
            },
            "&.Mui-error.Mui-focused": {
              boxShadow: `0 0 0 4px ${isDark ? "rgba(255,69,58,0.18)" : "rgba(208,52,44,0.12)"}`,
            },
          },
        },
      },
      MuiPickersInputBase: {
        styleOverrides: {
          root: {
            minHeight: formControlHeight,
            fontSize: "0.9375rem",
            letterSpacing: 0,
          },
          sizeSmall: { minHeight: formControlHeightSmall },
          sectionsContainer: {
            paddingBlock: 10,
            fontVariantNumeric: "lining-nums tabular-nums",
          },
        },
      },
      MuiPickersLayout: {
        styleOverrides: {
          root: {
            backgroundColor: colors.paper,
            borderRadius: shape.dialogRadius,
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            borderRadius: "50%",
            fontSize: "0.875rem",
            "&.Mui-selected": {
              backgroundColor: colors.primary,
              color: colors.primaryContrast,
              "&:hover": { backgroundColor: colors.primaryHover },
            },
          },
        },
      },
      MuiSelect: {
        defaultProps: { size: "medium" },
        styleOverrides: {
          select: {
            boxSizing: "border-box",
            minHeight: formControlHeight,
            paddingBlock: 10,
            display: "flex",
            alignItems: "center",
            "&.MuiInputBase-sizeSmall": {
              minHeight: formControlHeightSmall,
              paddingBlock: 6,
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 14,
            border: `1px solid ${colors.divider}`,
            boxShadow: colors.shadowMd,
            backgroundImage: "none",
          },
          list: { padding: 6 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            minHeight: formControlHeightSmall,
            paddingBlock: 10,
            fontSize: "0.9375rem",
            "&.Mui-selected": { backgroundColor: colors.primarySoft },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: colors.textTertiary,
            borderRadius: 6,
            "&.Mui-checked": { color: colors.primary },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: colors.textTertiary,
            "&.Mui-checked": { color: colors.primary },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: { width: 51, height: 31, padding: 0, margin: 6 },
          switchBase: {
            padding: 2,
            "&.Mui-checked": {
              transform: "translateX(20px)",
              color: colors.primaryContrast,
              "& + .MuiSwitch-track": {
                backgroundColor: isDark ? "#30d158" : "#34c759",
                opacity: 1,
              },
            },
          },
          thumb: {
            width: 27,
            height: 27,
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
            backgroundColor: colors.primaryContrast,
          },
          track: {
            borderRadius: 999,
            backgroundColor: isDark ? "#39393d" : "#e9e9ea",
            opacity: 1,
            transition: `background-color ${motion.base}`,
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          thumb: {
            width: 20,
            height: 20,
            backgroundColor: colors.primaryContrast,
            boxShadow: "0 1px 4px rgba(0, 0, 0, 0.24)",
            "&:hover, &.Mui-focusVisible": {
              boxShadow: `0 0 0 8px ${colors.primarySoft}`,
            },
          },
          rail: { opacity: 1, backgroundColor: colors.divider },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: colors.paper,
            backgroundImage: "none",
            border: `1px solid ${colors.dividerSoft}`,
            borderRadius: shape.surfaceRadius,
            boxShadow: "none",
          },
        },
        variants: [
          {
            props: { variant: "outlined" },
            style: {
              border: `1px solid ${colors.divider}`,
              boxShadow: "none",
            },
          },
          {
            props: { variant: "flat" },
            style: {
              backgroundColor: colors.tint,
              border: `1px solid ${colors.dividerSoft}`,
              boxShadow: "none",
            },
          },
          {
            props: { variant: "glass" },
            style: {
              backgroundColor: colors.glass,
              border: `1px solid ${colors.glassBorder}`,
              backdropFilter: "saturate(180%) blur(22px)",
              WebkitBackdropFilter: "saturate(180%) blur(22px)",
              boxShadow: colors.shadowMd,
            },
          },
        ],
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: colors.paper,
            backgroundImage: "none",
            border: `1px solid ${colors.dividerSoft}`,
            borderRadius: shape.cardRadius,
            boxShadow: "none",
            overflow: "hidden",
            transition: `transform ${motion.base}, box-shadow ${motion.base}, border-color ${motion.base}`,
          },
        },
        variants: [
          {
            props: { variant: "outlined" },
            style: {
              border: `1px solid ${colors.divider}`,
              boxShadow: "none",
            },
          },
        ],
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 22,
            "&:last-child": { paddingBottom: 22 },
          },
        },
      },
      MuiCardActionArea: {
        styleOverrides: {
          root: { "& .MuiCardActionArea-focusHighlight": { opacity: 0 } },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: colors.dividerSoft } },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: shape.dialogRadius,
            backgroundColor: colors.paper,
            backgroundImage: "none",
            border: `1px solid ${colors.dividerSoft}`,
            boxShadow: colors.shadowLg,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: isDark
              ? "rgba(0, 0, 0, 0.62)"
              : "rgba(0, 0, 0, 0.34)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            color: colors.text,
            fontFamily: fonts.displayFontFamily,
            fontWeight: 600,
            fontSize: "1.1875rem",
            letterSpacing: 0,
            padding: "20px 22px 8px",
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            color: colors.textSecondary,
            padding: "8px 22px",
            // every field inside a dialog shares one comfortable height,
            // regardless of how the form markup is nested
            "& .MuiOutlinedInput-root, & .MuiPickersOutlinedInput-root": {
              minHeight: formControlHeight,
            },
            "& .MuiOutlinedInput-root:not(.MuiInputBase-multiline):not(.MuiAutocomplete-inputRoot), & .MuiPickersOutlinedInput-root":
              { height: formControlHeight },
            "& .MuiOutlinedInput-input, & .MuiPickersInputBase-sectionsContainer":
              {
                paddingBlock: 10,
              },
            "& .MuiInputBase-multiline": { height: "auto" },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: { root: { padding: 22, paddingTop: 12, gap: 8 } },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.glass,
            color: colors.text,
            backgroundImage: "none",
            border: "none",
            borderRadius: 0,
            borderBottom: `1px solid ${colors.dividerSoft}`,
            boxShadow: "none",
            backdropFilter: "saturate(180%) blur(20px)",
            WebkitBackdropFilter: "saturate(180%) blur(20px)",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.paper,
            backgroundImage: "none",
            borderRadius: 0,
            borderColor: colors.dividerSoft,
            boxShadow: "none",
          },
        },
      },
      MuiToolbar: {
        styleOverrides: { root: { minHeight: 64 } },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            minHeight: controlHeight,
            margin: "2px 10px",
            paddingInline: 12,
            borderRadius: shape.controlRadius,
            color: colors.textSecondary,
            transition: `background-color ${motion.fast}, color ${motion.fast}`,
            "& .MuiListItemIcon-root": { color: "inherit", minWidth: 34 },
            "&:hover": {
              color: colors.text,
              backgroundColor: colors.primarySoft,
            },
            "&.Mui-selected": {
              color: colors.primary,
              backgroundColor: colors.primarySoft,
              "&:hover": { backgroundColor: colors.primarySoft },
            },
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontSize: "0.9063rem",
            fontWeight: 500,
            letterSpacing: 0,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: "999px !important",
            minHeight: 26,
            fontWeight: 500,
            fontSize: "0.8125rem",
            letterSpacing: 0,
            borderColor: colors.divider,
          },
          sizeSmall: { minHeight: 22, fontSize: "0.75rem" },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 14,
            border: `1px solid ${colors.dividerSoft}`,
            boxShadow: "none",
          },
          message: { fontSize: "0.9063rem" },
          standardInfo: { backgroundColor: colors.primarySoft },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 8,
            fontSize: "0.75rem",
            fontWeight: 500,
            padding: "6px 10px",
            backgroundColor: isDark
              ? "rgba(58, 58, 60, 0.96)"
              : "rgba(29, 29, 31, 0.92)",
            backdropFilter: "blur(10px)",
          },
          arrow: {
            color: isDark ? "rgba(58, 58, 60, 0.96)" : "rgba(29, 29, 31, 0.92)",
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 42 },
          indicator: { height: 2, borderRadius: 2 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 42,
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.9375rem",
            letterSpacing: 0,
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 999, height: 6 },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: `${shape.surfaceRadius}px !important`,
            border: `1px solid ${colors.dividerSoft}`,
            boxShadow: "none",
            backgroundImage: "none",
            "&::before": { display: "none" },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: { borderTop: `1px solid ${colors.dividerSoft}` },
          selectLabel: { color: colors.textSecondary },
          displayedRows: { color: colors.textSecondary },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            width: "100%",
            overflowX: "auto",
            borderRadius: shape.surfaceRadius,
            scrollbarWidth: "thin",
            border: `1px solid ${colors.dividerSoft}`,
            backgroundColor: colors.paper,
            backgroundImage: "none",
            boxShadow: "none",
            "&::-webkit-scrollbar": { height: 8 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: colors.tableScrollbar,
              borderRadius: 999,
            },
            [mobileQuery]: { boxShadow: "none" },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
            fontSize: "0.6875rem",
            color: colors.textSecondary,
            letterSpacing: "0.06em",
            borderBottom: `1px solid ${colors.divider}`,
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            wordBreak: "normal",
            overflowWrap: "normal",
            lineHeight: 1.25,
            backgroundColor: colors.tableHead,
            [mobileQuery]: { fontSize: "0.6875rem" },
          },
          body: {
            fontSize: "0.875rem",
            color: colors.text,
            borderBottom: `1px solid ${colors.dividerSoft}`,
          },
          root: {
            paddingTop: 10,
            paddingBottom: 10,
            whiteSpace: "normal",
            wordBreak: "break-word",
            [mobileQuery]: { padding: "10px 9px", fontSize: "0.875rem" },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            height: size.tableRow,
            transition: `background-color ${motion.fast}`,
            "&:nth-of-type(odd)": { backgroundColor: colors.tableStripe },
            "&:hover": { backgroundColor: colors.tableHover },
            "&:last-of-type .MuiTableCell-body": { borderBottom: 0 },
            [mobileQuery]: { height: "auto" },
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            borderRadius: shape.surfaceRadius,
            overflow: "hidden",
            width: "100%",
            tableLayout: "fixed",
          },
        },
      },
      MuiLink: {
        defaultProps: { underline: "hover" },
        styleOverrides: {
          root: {
            color: colors.link,
            textDecorationColor: "transparent",
            transition: `color ${motion.fast}, text-decoration-color ${motion.fast}`,
            "&:hover": { textDecorationColor: "currentColor" },
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: { backgroundColor: colors.tintStrong, borderRadius: 8 },
        },
      },
    },
  });
}
